import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { revenueApi } from "@/lib/api";
import { contractSelectionParams, isEmptySelection, type ContractSelection } from "@/lib/royalty";
import { useOrganizationStore } from "@/stores/organizationStore";

interface UseRoyaltyStatementPreviewParams {
  selection: ContractSelection;
  startDate?: Date;
  endDate?: Date;
  /** 1-based page. */
  page?: number;
  /** Rows per page; the API default (50) when omitted. */
  pageSize?: number;
  enabled?: boolean;
}

/**
 * One page of the (contract, payee) rows a bulk run would create, with ledger
 * amounts; `count` is the full total. Keyed like the summary/checks queries so
 * all three describe the same selection.
 */
export function useRoyaltyStatementPreview({
  selection,
  startDate,
  endDate,
  page = 1,
  pageSize = 50,
  enabled = true,
}: UseRoyaltyStatementPreviewParams) {
  const { currentLabel } = useOrganizationStore();
  const periodStart = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const periodEnd = endDate ? format(endDate, "yyyy-MM-dd") : "";
  const params = contractSelectionParams(selection);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      "royalty-statement-preview",
      currentLabel?.id,
      periodStart,
      periodEnd,
      selection.mode,
      [...selection.contracts].sort().join(","),
      page,
      pageSize,
    ],
    queryFn: () =>
      revenueApi.getRoyaltyStatementPreview({
        label_id: currentLabel?.id as string,
        period_start: periodStart,
        period_end: periodEnd,
        page,
        page_size: pageSize,
        ...params,
      }),
    enabled:
      enabled &&
      !!currentLabel &&
      !!startDate &&
      !!endDate &&
      startDate <= endDate &&
      !isEmptySelection(selection),
    // Ticking changes the key; keep the last rows on screen mid-fetch.
    placeholderData: keepPreviousData,
  });

  return { rows: data?.results ?? [], count: data?.count ?? 0, isLoading, isError, isFetching };
}
